import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { partnerQueryKeys } from "@/hooks/partners"
import { Subscribers } from "@/services/partnerApi"
import type { Subscriber } from "@/services/partnerApi"

const queryKeys = {
  all: ["subscribers"] as const,
  detail: (id: string) => [...queryKeys.all, "detail", id] as const,
}

export function useSubscribers(name?: string, partnerId?: string) {
  return useQuery({
    queryKey: queryKeys.all,

    staleTime: 1000 * 60 * 5,

    queryFn: async () => {
      let subscribers: Array<Subscriber> = []
      const pagination = { offset: 0, limit: 200 }

      while (true) {
        const response = await Subscribers.ListSubscribers({ pagination })
        if (response.subscribers.length === 0) {
          break
        }
        subscribers = subscribers.concat(response.subscribers)
        if (response.subscribers.length < pagination.limit) {
          break
        }
        pagination.offset += response.subscribers.length
      }

      return subscribers
    },

    select: (subscribers) =>
      subscribers.filter((s) => {
        const conditions = []

        if (name) {
          conditions.push(s.name?.toLowerCase().includes(name.toLowerCase()))
        }

        if (partnerId) {
          conditions.push(s.partner_id === partnerId)
        }

        return conditions.every(Boolean)
      }),
  })
}

export function useSubscriber(id: string) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: queryKeys.detail(id),

    queryFn: () => Subscribers.GetSubscriber({ id }).then((r) => r.subscriber),

    initialData: () =>
      queryClient
        .getQueryData<Array<Subscriber>>(queryKeys.all)
        ?.find((s) => s.id === id),

    initialDataUpdatedAt: () =>
      queryClient.getQueryState(queryKeys.all)?.dataUpdatedAt,
  })
}

export function useCreateSubscriber() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: Subscribers.CreateSubscriber,

    onMutate: async (newSubscriber) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.all })
      const previous = queryClient.getQueryData(queryKeys.all)
      queryClient.setQueryData<Array<Subscriber>>(queryKeys.all, (old) => {
        const sub = { ...newSubscriber, id: "new" }
        return old ? [...old, sub] : [sub]
      })
      return { previous }
    },

    onError: (_err, _newSubscriber, context) => {
      if (context) {
        queryClient.setQueryData(queryKeys.all, context.previous)
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.all })
      queryClient.invalidateQueries({ queryKey: partnerQueryKeys.all })
    },
  })
}

export function useUpdateSubscriber() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: Subscribers.UpdateSubscriber,

    onMutate: async (newSubscriber) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.all })
      const previous = queryClient.getQueryData(queryKeys.all)
      queryClient.setQueryData<Array<Subscriber>>(queryKeys.all, (old) => {
        return old?.map((s) => (s.id === newSubscriber.id ? newSubscriber : s))
      })
      return { previous }
    },

    onError: (_err, _newSubscriber, context) => {
      if (context) {
        queryClient.setQueryData(queryKeys.all, context.previous)
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.all })
    },
  })
}

export function useDeleteSubscriber() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: Subscribers.DeleteSubscriber,

    onMutate: async (subscriber) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.all })
      await queryClient.removeQueries({
        queryKey: queryKeys.detail(subscriber.id),
      })
      const previous = queryClient.getQueryData(queryKeys.all)
      queryClient.setQueryData<Array<Subscriber>>(queryKeys.all, (old) => {
        return old?.filter((s) => s.id !== subscriber.id)
      })
      return { previous }
    },

    onError: (_err, _subscriber, context) => {
      if (context) {
        queryClient.setQueryData(queryKeys.all, context.previous)
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.all })
      queryClient.invalidateQueries({ queryKey: partnerQueryKeys.all })
    },
  })
}
